import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useMenuRoles } from "../auth/MenuRoleContext";
import { useLanguage } from "../ui/LanguageProvider";

/**
 * 상단 업데이트 알림 배너 — "이런 게 생겼습니다"를 한 줄로 알린다.
 *
 *  ★ 내용은 사람이 등록한다(/settings/announcements). 릴리스 노트 자동 생성이 아니다.
 *
 *  ★ 앱 영역 최상단 — AppContextBar(페이지명 바) 위에 붙는다. 공지는 페이지보다 바깥 소식이다.
 *
 *  ★ sticky 를 쓰지 않는다. 아래 AppContextBar 가 sticky top:0 이라, 스크롤하면 배너가 먼저
 *    올라가 사라지고 바가 제자리에 고정된다. 배너까지 고정하면 캔버스·표·간트 화면에서
 *    40px 을 영구히 먹는다 — 스크롤 한 번으로 공간이 돌아오는 편이 낫다.
 *
 *  ★ 배지에 빨강·느낌표를 쓰지 않는다. 알림 벨의 빨간 점이 "조치 필요"라는 뜻이고,
 *    공지까지 빨강이면 그 신호가 흔해져 무시하게 된다(NotificationBell 주석과 같은 이유).
 *    바탕은 연파랑(primary-subtle) + 아래 hairline — 회색 상단 바와 떨어져 보인다.
 *    배지 색은 종류별: NEW 파랑 · 개선 초록 · 점검 노랑 · 공지 회색(글자는 *-strong).
 *
 *  ★ 한 줄에 한 건씩, 여러 건이면 '‹ 1/3 ›' 로 넘겨 본다. 좁은 폭에서는 본문·링크문구가
 *    먼저 숨으므로(아래 media query) 화살표가 제목을 밀어내지 않는다.
 *    5초마다 아래에서 올라오며 자동으로 넘어가고, 마우스를 올리거나 포커스가 들어오면 멈춘다.
 */

export type Announcement = {
  id: string;
  badge: string;
  title: string;
  body?: string | null;
  link_path?: string | null;
  link_label?: string | null;
  link_menu_key?: string | null;
};

const BANNER_HEIGHT = 40;
const ROTATE_MS = 5000;

// 세션 한정 숨김("닫기"). 새로고침하면 사라지는 게 사양이라 저장하지 않는다 — 메모리면 충분하다.
const closedThisSession = new Set<string>();

// 배지 값은 저장값(NEW|개선|공지|점검) — 표시만 영문으로 바꾼다.
const BADGE_EN: Record<string, string> = { 개선: "Improved", 공지: "Notice", 점검: "Maintenance" };

// 배지 종류별 색(알약). 모르는 값은 공지(회색)로.
const BADGE_TONE: Record<string, { bg: string; color: string }> = {
  NEW: { bg: "var(--primary-subtle)", color: "var(--primary-strong)" },
  개선: { bg: "var(--success-subtle)", color: "var(--success-strong)" },
  점검: { bg: "var(--warning-subtle)", color: "var(--warning-strong)" },
  공지: { bg: "var(--neutral-subtle)", color: "#464646" /* ink-data */ },
};

function isExternal(path: string): boolean {
  return /^https?:\/\//i.test(path);
}

export default function AnnouncementBanner() {
  const { t, language } = useLanguage();
  const { email } = useAuth();
  const { visible, loaded } = useMenuRoles();
  const navigate = useNavigate();
  const [items, setItems] = useState<Announcement[]>([]);
  const [cursor, setCursor] = useState(0);
  const [paused, setPaused] = useState(false);
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!email) {
      setItems([]);
      return;
    }
    let alive = true;
    fetch("/api/auth-users/announcements", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => {
        if (alive) setItems(d.announcements || []);
      })
      .catch(() => {
        // 배너는 부가 기능이다. 실패하면 조용히 없는 것처럼 둔다.
        if (alive) setItems([]);
      });
    return () => {
      alive = false;
    };
  }, [email]);

  // 권한 없는 경로의 배너는 아예 안 띄운다 — 눌렀는데 /home 으로 튕기면 배너가 거짓말을 한 셈이다.
  // 메뉴 매핑이 아직 안 왔으면(loaded=false) 판단을 미룬다. 성급히 보여주면 잠깐 새어나간다.
  const shown = items.filter((a) => {
    if (closedThisSession.has(a.id)) return false;
    if (!a.link_menu_key) return true;
    return loaded && visible(a.link_menu_key);
  });

  // 목록이 줄어들면(닫기·다시 보지 않기) 커서가 범위 밖으로 나간다 — 저장값 대신 이 값을 쓴다.
  const index = shown.length ? Math.min(cursor, shown.length - 1) : 0;
  const current = shown[index];
  const step = (d: number) => setCursor((shown.length + index + d) % shown.length);

  // 5초마다 자동으로 다음 공지. index 가 바뀌면 타이머도 새로 잡히므로 화살표를 누르면 5초가 리셋된다.
  // 마우스를 올린 동안은 멈춘다 — 읽는 중에 바뀌거나, 누르려는 순간 다른 공지로 넘어가면 최악이다.
  const total = shown.length;
  useEffect(() => {
    if (total < 2 || paused) return;
    const t = setTimeout(() => setCursor((index + 1) % total), ROTATE_MS);
    return () => clearTimeout(t);
  }, [total, index, paused]);

  // 본문 높이를 vh 로 직접 계산하는 화면들이 배너 높이를 뺄 수 있게 실측값을 내보낸다.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--announce-h", current ? `${BANNER_HEIGHT}px` : "0px");
    return () => root.style.setProperty("--announce-h", "0px");
  }, [current]);

  const close = useCallback((id: string) => {
    closedThisSession.add(id);
    forceRender((n) => n + 1);
  }, []);

  // "다시 보지 않기" — 낙관적으로 먼저 지운다. 서버가 실패하면 다음 로드에서 다시 뜬다.
  // 그 편이 버튼을 눌렀는데 배너가 남아 있는 것보다 낫다(눌린 건지 아닌지 알 수 없다).
  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((a) => a.id !== id));
    fetch("/api/auth-users/announcement-dismiss", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    }).catch(() => { /* 부가 기능이다. 실패해도 화면을 막지 않는다 */ });
  }, []);

  if (!current) return null;

  const path = current.link_path || "";
  const clickable = Boolean(path);
  const external = clickable && isExternal(path);

  // ★ 진짜 <a href> 다. role="link" 를 흉내 낸 div 가 아니라서 포커스·Enter·가운데 클릭·
  //   "링크 주소 복사"가 전부 브라우저 기본 동작으로 공짜로 붙는다.
  //   내부 경로는 href 를 그대로 두고 클릭만 가로채 라우터로 보낸다(전체 새로고침 방지).
  const onClick = (e: React.MouseEvent) => {
    if (external) return; // 새 탭은 브라우저에 맡긴다
    // 새 탭/새 창으로 열려는 클릭은 가로채지 않는다.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(path);
  };

  const label = (
    <>
      <span style={{ ...badgeStyle, ...(BADGE_TONE[current.badge] ?? BADGE_TONE["공지"]) }}>{language === "en" ? BADGE_EN[current.badge] || current.badge : current.badge}</span>
      <span style={titleStyle}>{current.title}</span>
      {current.body ? <span className="announce-body" style={bodyStyle}>{current.body}</span> : null}
      {clickable ? <span className="announce-cta" style={chevronStyle}>{current.link_label || "›"}</span> : null}
    </>
  );

  return (
    <div
      style={barStyle}
      // 자동으로 돌아가는 줄을 aria-live 로 읽어 주면 5초마다 스크린리더가 말을 끊는다. 단건일 때만 알린다.
      role={total > 1 ? "region" : "status"}
      aria-label={total > 1 ? t("공지", "Announcements") : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* 좁은 폭에서 본문·링크문구가 제목과 공간을 나눠 가져 셋 다 뭉개진다. 제목이 이긴다.
          (1440·1024 는 전부 들어간다 — 실측) 줄 전체가 클릭 대상이라 링크 문구가 없어도 동작은 같다.
          아래에서 올라오는 전환: 배너가 overflow:hidden 이라 40px 밖은 잘린다. key 가 바뀌며 다시 마운트될
          때만 재생되므로 상태를 따로 들 필요가 없다. 모션을 끈 사용자에겐 안 움직인다. */}
      <style>{ROTATE_CSS}</style>
      {clickable ? (
        <a
          key={current.id}
          className="announce-slide"
          href={path}
          onClick={onClick}
          target={external ? "_blank" : undefined}
          rel={external ? "noopener noreferrer" : undefined}
          style={{ ...contentStyle, cursor: "pointer", textDecoration: "none", color: "inherit" }}
        >
          {label}
        </a>
      ) : (
        <div key={current.id} className="announce-slide" style={contentStyle}>{label}</div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
        {shown.length > 1 ? (
          <>
            <button type="button" onClick={() => step(-1)} title={t("이전 공지", "Previous announcement")} aria-label={t("이전 공지", "Previous announcement")} style={iconBtnStyle}>
              <ChevronLeft size={14} />
            </button>
            <span style={counterStyle}>{index + 1}/{shown.length}</span>
            <button type="button" onClick={() => step(1)} title={t("다음 공지", "Next announcement")} aria-label={t("다음 공지", "Next announcement")} style={iconBtnStyle}>
              <ChevronRight size={14} />
            </button>
          </>
        ) : null}
        <button type="button" onClick={() => dismiss(current.id)} style={textBtnStyle}>
          {t("다시 보지 않기", "Don't show again")}
        </button>
        <button type="button" onClick={() => close(current.id)} title={t("닫기", "Close")} aria-label={t("닫기", "Close")} style={iconBtnStyle}>
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

const ROTATE_CSS = [
  "@media (max-width:860px){.announce-body{display:none}}",
  "@media (max-width:560px){.announce-cta{display:none}}",
  "@keyframes announce-rise{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}",
  ".announce-slide{animation:announce-rise .34s cubic-bezier(.22,1,.36,1)}",
  "@media (prefers-reduced-motion:reduce){.announce-slide{animation:none}}",
].join("");

const barStyle: React.CSSProperties = {
  height: BANNER_HEIGHT,
  overflow: "hidden", // 올라오는 줄이 배너 밖으로 새어 보이지 않게
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  padding: "0 12px 0 16px",
  fontSize: 13,
  // 회색 표면 토큰을 쓰면 아래 AppContextBar 와 붙어 한 덩어리로 보인다 — 연파랑으로 떼어 놓는다.
  //   NEW 배지(연파랑)는 흰 테두리 대신 바탕보다 진한 글자로 구분된다.
  background: "var(--primary-subtle)",
  borderBottom: "1px solid var(--hairline)",
};

const counterStyle: React.CSSProperties = {
  flexShrink: 0,
  minWidth: 28,
  textAlign: "center",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--ink-muted)",
  fontFamily: "var(--font-num)",
  fontVariantNumeric: "tabular-nums",
};

const contentStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
  flex: 1,
  height: "100%",
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
  textAlign: "left",
};

const badgeStyle: React.CSSProperties = {
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  height: 22,
  padding: "0 8px",
  borderRadius: "var(--radius-pill)",
  fontSize: 12,
  fontWeight: 600,
};

const titleStyle: React.CSSProperties = {
  minWidth: 0,
  flexShrink: 1,
  fontSize: 13,
  fontWeight: 600,
  color: "var(--ink)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const bodyStyle: React.CSSProperties = {
  minWidth: 0,
  // 자리가 모자라면 본문이 먼저 줄어든다 — 제목보다 덜 중요하다.
  flexShrink: 8,
  fontSize: 13,
  color: "var(--ink-muted)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const chevronStyle: React.CSSProperties = {
  flexShrink: 0,
  fontSize: 13,
  fontWeight: 600,
  color: "var(--primary-strong)",
};

const textBtnStyle: React.CSSProperties = {
  height: 28,
  padding: "0 8px",
  border: "none",
  borderRadius: 6,
  background: "transparent",
  color: "var(--ink-muted)",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const iconBtnStyle: React.CSSProperties = {
  // 배너 40px 안의 좁은 자리라 28px(FITTS_LAW 좁은 곳 기준).
  height: 28,
  width: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  borderRadius: 6,
  background: "transparent",
  color: "var(--ink-muted)",
  cursor: "pointer",
  fontFamily: "inherit",
  flexShrink: 0,
};
